/**
 * Spec Phase 4 Slice 4F — SpecSettings.
 *
 * Admin-only page at view === 'spec-settings' (App.js routes).
 * Mirrors DrawingSettings.tsx shape, scoped to the 3 spec tiers
 * (spec_capable / spec_fast / spec_fallback) plus the 3 default
 * rows (jurisdiction / word_target_min / word_target_max) per
 * brief §6.
 *
 * Reads /api/admin/spec/config + /api/admin/spec/models/available
 * on mount; saves via PATCH /api/admin/spec/config — one PATCH per
 * tier diff, one PATCH per defaults diff. Server enforces
 * tier-must-start-with-spec_ + admin-role.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, AlertTriangle, RotateCcw, Save, ExternalLink } from 'lucide-react';
import {
  getSpecConfig,
  getSpecModelsAvailable,
  patchSpecConfig,
  type SpecConfigDefaultsPatch,
  type SpecConfigResponse,
  type SpecJurisdiction,
  type SpecModelsAvailableResponse,
} from '@/api/client';

interface Props {
  /** Logged-in user role — bounce non-admins with a 403 panel. */
  userRole: string | undefined;
  /** Back to whichever view called settings (typically the Spec
   * workbench). */
  onBack?: () => void;
  /** Open the existing prompts admin page (filtered to stage='spec'
   * via the URL anchor or just bouncing the drafter to /admin/prompts
   * since the existing PromptAdmin already filters). Wired by App.js
   * mirroring the `onOpenPrompts` prop used elsewhere. */
  onOpenPrompts?: () => void;
}

type SpecTierKey = 'spec_capable' | 'spec_fast' | 'spec_fallback';

/** Fallback provider catalogue, used only if the backend's
 * /admin/spec/models/available call fails. The authoritative list is
 * llm.DEFAULT_MODELS, served by that endpoint; this copy exists so the
 * dropdown is never empty and so admins can configure a provider whose
 * key is not entered yet. Keep it in step with llm.py — the backend
 * test tests/spec/test_provider_catalogue_parity.py enforces the
 * server side, and SpecSettings.test.tsx pins this list. */
const PROVIDER_CATALOG = [
  'anthropic', 'openai', 'google', 'openrouter',
  'fireworks', 'nvidia', 'local', 'custom',
] as const;

interface TierRow {
  key: SpecTierKey;
  label: string;
  helpText: string;
  defaultProvider: string;
  defaultModel: string;
}

/** Defaults match the values seeded by seeds/system_config.py
 * (Phase 1 Slice 1B). "Reset to defaults" only resets in-memory
 * state; user must click Save to persist. */
const TIER_ROWS: TierRow[] = [
  {
    key: 'spec_capable',
    label: 'Capable tier',
    helpText:
      'Used for section drafting, examiner review, paragraph expand/tighten, NL edits.',
    defaultProvider: 'anthropic',
    defaultModel: 'claude-sonnet-4-6',
  },
  {
    key: 'spec_fast',
    label: 'Fast tier',
    helpText: 'Used for section verification.',
    defaultProvider: 'anthropic',
    defaultModel: 'claude-haiku-4-5',
  },
  {
    key: 'spec_fallback',
    label: 'Fallback',
    helpText: 'Used when the primary capable call fails.',
    defaultProvider: 'anthropic',
    defaultModel: 'claude-haiku-4-5',
  },
];

const WORD_TARGET_JURISDICTIONS: SpecJurisdiction[] = ['EP', 'US', 'IN'];

type JurisdictionTargets = Record<
  SpecJurisdiction, { min: number | ''; max: number | '' }
>;

const emptyJurisdictionTargets: JurisdictionTargets = {
  EP: { min: '', max: '' },
  US: { min: '', max: '' },
  IN: { min: '', max: '' },
};

/** Read the per-jurisdiction block off a config response. A
 * jurisdiction that is inheriting the global default shows blank
 * inputs, so saving nothing leaves it inheriting. */
function targetsFromConfig(cfg: SpecConfigResponse): JurisdictionTargets {
  const out = { ...emptyJurisdictionTargets };
  const block = cfg.defaults.by_jurisdiction;
  for (const code of WORD_TARGET_JURISDICTIONS) {
    const row = block?.[code];
    out[code] = row && !row.inherited
      ? { min: row.word_target_min ?? '', max: row.word_target_max ?? '' }
      : { min: '', max: '' };
  }
  return out;
}

/** Thinking budget offered on this page. 'minimal' and 'none' are
 * left out on purpose: measured on Fireworks glm-5p3, 'minimal' is
 * rejected outright (a wasted request, then the provider default) and
 * 'none' produced MORE thinking than the default. */
const REASONING_EFFORTS: { value: string; label: string }[] = [
  { value: '', label: 'Provider default (slowest)' },
  { value: 'low', label: 'Low — recommended' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const DEFAULT_DEFAULTS = {
  jurisdiction: 'EP' as 'EP' | 'US' | 'IN',
  reasoning_effort: 'low',
  word_target_min: 5000,
  word_target_max: 7000,
};

interface TierDraft {
  provider: string;
  model: string;
}

const outlineBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 ' +
  'bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 ' +
  'transition-colors';

const primaryBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm bg-zinc-900 px-3 ' +
  'text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50 transition-colors';

export default function SpecSettings({
  userRole,
  onBack,
  onOpenPrompts,
}: Props): JSX.Element {
  const isAdmin = userRole === 'admin';
  const [config, setConfig] = useState<SpecConfigResponse | null>(null);
  const [providers, setProviders] = useState<SpecModelsAvailableResponse['providers']>([]);
  const [drafts, setDrafts] = useState<Record<SpecTierKey, TierDraft>>({
    spec_capable: { provider: '', model: '' },
    spec_fast: { provider: '', model: '' },
    spec_fallback: { provider: '', model: '' },
  });
  const [defaultsDraft, setDefaultsDraft] = useState({
    jurisdiction: DEFAULT_DEFAULTS.jurisdiction,
    word_target_min: DEFAULT_DEFAULTS.word_target_min,
    word_target_max: DEFAULT_DEFAULTS.word_target_max,
    reasoning_effort: DEFAULT_DEFAULTS.reasoning_effort,
  });
  // Per-jurisdiction word targets. A specification's expected length
  // is an office-level convention, so each jurisdiction carries its
  // own pair; the global pair above is the fallback for a jurisdiction
  // with no row of its own.
  const [targetsDraft, setTargetsDraft] = useState<JurisdictionTargets>(
    emptyJurisdictionTargets,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const hydrate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, mods] = await Promise.all([
        getSpecConfig(),
        getSpecModelsAvailable(),
      ]);
      setConfig(cfg);
      setProviders(mods.providers);
      setDrafts({
        spec_capable: { ...cfg.tiers.spec_capable },
        spec_fast: { ...cfg.tiers.spec_fast },
        spec_fallback: { ...cfg.tiers.spec_fallback },
      });
      setDefaultsDraft({
        jurisdiction:
          (cfg.defaults.jurisdiction as 'EP' | 'US' | 'IN' | null) ??
          DEFAULT_DEFAULTS.jurisdiction,
        word_target_min:
          cfg.defaults.word_target_min ?? DEFAULT_DEFAULTS.word_target_min,
        word_target_max:
          cfg.defaults.word_target_max ?? DEFAULT_DEFAULTS.word_target_max,
        reasoning_effort: cfg.defaults.reasoning_effort ?? '',
      });
      setTargetsDraft(targetsFromConfig(cfg));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ??
        (e as { message?: string })?.message ??
        'Failed to load spec settings.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    void hydrate();
  }, [hydrate, isAdmin]);

  // ── Diff detection ─────────────────────────────────────────────
  const tierDiffs = useMemo(() => {
    if (!config) return [] as SpecTierKey[];
    const dirty: SpecTierKey[] = [];
    for (const row of TIER_ROWS) {
      const a = config.tiers[row.key];
      const b = drafts[row.key];
      if (!a || !b) continue;
      if (a.provider !== b.provider || a.model !== b.model) {
        dirty.push(row.key);
      }
    }
    return dirty;
  }, [config, drafts]);

  const defaultsDiff = useMemo(() => {
    if (!config) return null;
    const d: Partial<typeof defaultsDraft> = {};
    if (config.defaults.jurisdiction !== defaultsDraft.jurisdiction) {
      d.jurisdiction = defaultsDraft.jurisdiction;
    }
    if (
      (config.defaults.word_target_min ?? null) !== defaultsDraft.word_target_min
    ) {
      d.word_target_min = defaultsDraft.word_target_min;
    }
    if (
      (config.defaults.word_target_max ?? null) !== defaultsDraft.word_target_max
    ) {
      d.word_target_max = defaultsDraft.word_target_max;
    }
    if (
      (config.defaults.reasoning_effort ?? '') !== defaultsDraft.reasoning_effort
    ) {
      d.reasoning_effort = defaultsDraft.reasoning_effort;
    }
    return Object.keys(d).length > 0 ? d : null;
  }, [config, defaultsDraft]);

  // Per-jurisdiction diff. A cleared input sends null, which drops the
  // row and puts that jurisdiction back on the global default.
  const targetsDiff = useMemo(() => {
    if (!config) return null;
    const saved = targetsFromConfig(config);
    const out: NonNullable<
      SpecConfigDefaultsPatch['defaults']['by_jurisdiction']
    > = {};
    for (const code of WORD_TARGET_JURISDICTIONS) {
      const before = saved[code];
      const after = targetsDraft[code];
      if (before.min === after.min && before.max === after.max) continue;
      out[code] = {
        word_target_min: after.min === '' ? null : after.min,
        word_target_max: after.max === '' ? null : after.max,
      };
    }
    return Object.keys(out).length > 0 ? out : null;
  }, [config, targetsDraft]);

  const isDirty =
    tierDiffs.length > 0 || defaultsDiff !== null || targetsDiff !== null;

  const handleSave = useCallback(async () => {
    if (!config || saving || !isDirty) return;
    setSaving(true);
    setError(null);
    try {
      // One PATCH per dirty tier (server requires the XOR shape).
      let latest: SpecConfigResponse = config;
      for (const tier of tierDiffs) {
        const draft = drafts[tier];
        latest = await patchSpecConfig({
          tier,
          provider: draft.provider,
          model: draft.model,
        });
      }
      // One PATCH for the defaults if any changed — the global pair,
      // the default jurisdiction and the per-jurisdiction targets all
      // ride in the same defaults body.
      if (defaultsDiff || targetsDiff) {
        latest = await patchSpecConfig({
          defaults: {
            ...(defaultsDiff ?? {}),
            ...(targetsDiff ? { by_jurisdiction: targetsDiff } : {}),
          },
        });
      }
      setConfig(latest);
      setDrafts({
        spec_capable: { ...latest.tiers.spec_capable },
        spec_fast: { ...latest.tiers.spec_fast },
        spec_fallback: { ...latest.tiers.spec_fallback },
      });
      setDefaultsDraft({
        jurisdiction:
          (latest.defaults.jurisdiction as 'EP' | 'US' | 'IN' | null) ??
          DEFAULT_DEFAULTS.jurisdiction,
        word_target_min:
          latest.defaults.word_target_min ?? DEFAULT_DEFAULTS.word_target_min,
        word_target_max:
          latest.defaults.word_target_max ?? DEFAULT_DEFAULTS.word_target_max,
        reasoning_effort: latest.defaults.reasoning_effort ?? '',
      });
      setTargetsDraft(targetsFromConfig(latest));
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: { detail?: string } } }; message?: string })
          ?.response?.data?.detail?.detail ??
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
        (e as { message?: string })?.message ??
        'Save failed.';
      setError(typeof msg === 'string' ? msg : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [
    config, drafts, defaultsDraft, tierDiffs, defaultsDiff, targetsDiff,
    isDirty, saving,
  ]);

  const handleReset = useCallback(() => {
    setDrafts({
      spec_capable: {
        provider: TIER_ROWS[0].defaultProvider,
        model: TIER_ROWS[0].defaultModel,
      },
      spec_fast: {
        provider: TIER_ROWS[1].defaultProvider,
        model: TIER_ROWS[1].defaultModel,
      },
      spec_fallback: {
        provider: TIER_ROWS[2].defaultProvider,
        model: TIER_ROWS[2].defaultModel,
      },
    });
    setDefaultsDraft({ ...DEFAULT_DEFAULTS });
    setTargetsDraft({ ...emptyJurisdictionTargets });
  }, []);

  // ── Provider → models map for the cascading dropdown ──────────
  const modelsByProvider = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const p of providers) {
      map[p.id] = p.models.map((m) => m.id);
    }
    return map;
  }, [providers]);

  // ── Auth gate (UX nicety; require_admin on the server is the SoT).
  // Lives after the hooks so React's hook-call ordering stays
  // identical across renders regardless of role.
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <div className="border-b border-zinc-200 bg-white px-6 py-3">
          <h1 className="text-lg font-semibold text-zinc-900">
            Specification settings
          </h1>
        </div>
        <div
          className="mx-auto max-w-xl px-6 py-16 text-center"
          data-testid="spec-settings-403"
        >
          <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-amber-500" aria-hidden />
          <h2 className="mb-2 text-base font-semibold text-zinc-900">
            Admin access required
          </h2>
          <p className="text-sm text-zinc-600">
            Spec module settings are admin-only. Speak to your workspace admin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="spec-settings">
      <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              type="button"
              className={outlineBtn}
              onClick={onBack}
              data-testid="spec-settings-back"
              aria-label="Back to spec workbench"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          )}
          <h1 className="text-lg font-semibold text-zinc-900">
            Specification settings
          </h1>
          {savedAt && !isDirty && (
            <span className="text-xs text-zinc-400">Saved at {savedAt}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={outlineBtn}
            onClick={handleReset}
            disabled={loading || saving}
            data-testid="spec-settings-reset"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to defaults
          </button>
          <button
            type="button"
            className={primaryBtn}
            onClick={handleSave}
            disabled={loading || saving || !isDirty}
            data-testid="spec-settings-save"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save changes
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
        </div>
      ) : error ? (
        <div
          className="mx-auto mt-12 max-w-xl rounded-sm border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
          data-testid="spec-settings-error"
        >
          {error}
        </div>
      ) : (
        <div className="mx-auto max-w-3xl space-y-8 px-6 py-8">
          {/* ── Model selection ──────────────────────────────── */}
          <section data-testid="spec-settings-tiers">
            <h2 className="mb-1 text-base font-semibold text-zinc-900">
              Model selection
            </h2>
            <p className="mb-4 text-xs text-zinc-500">
              These models are used for spec-module LLM calls only.
              Drawing-side and claims-side models are independent.
            </p>
            <div className="space-y-3">
              {TIER_ROWS.map((row) => {
                const draft = drafts[row.key];
                // Cue follow-up — Spec settings exposes the same
                // 7-provider catalogue claims-side admin already
                // honours (Phase 13C). Backend-discovered providers
                // (those with valid API keys) merge in first; any
                // missing entries from the canonical list append so
                // admins can configure NVIDIA / Local / Custom even
                // before keys are entered.
                const discovered = providers.map((p) => p.id);
                const providerOptions = Array.from(new Set([
                  ...discovered,
                  ...PROVIDER_CATALOG,
                ]));
                const knownModels = modelsByProvider[draft.provider] ?? [];
                return (
                  <div
                    key={row.key}
                    className="rounded-sm border border-zinc-200 bg-white p-4"
                    data-testid={`spec-tier-row-${row.key}`}
                  >
                    <div className="mb-2 flex items-baseline justify-between">
                      <span className="text-sm font-medium text-zinc-900">
                        {row.label}
                      </span>
                      <span className="font-mono text-[10px] text-zinc-400">
                        {row.key}
                      </span>
                    </div>
                    <p className="mb-3 text-xs text-zinc-500">{row.helpText}</p>
                    <div className="flex items-center gap-3">
                      <label className="flex flex-1 flex-col gap-1">
                        <span className="text-[11px] font-medium text-zinc-600">
                          Provider
                        </span>
                        <select
                          className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-sm"
                          value={draft.provider}
                          onChange={(e) => {
                            const provider = e.target.value;
                            setDrafts((d) => {
                              const knownForNew =
                                modelsByProvider[provider] ?? [];
                              // Keep the admin-typed model when the
                              // new provider has no known catalogue
                              // (NVIDIA / Local / Custom — anything
                              // is valid). Auto-pick a sensible
                              // default only when the provider is
                              // backend-discovered and the previously
                              // selected model isn't in its catalogue.
                              const prevModel = d[row.key].model;
                              const nextModel = knownForNew.length === 0
                                ? prevModel
                                : (knownForNew.includes(prevModel)
                                  ? prevModel
                                  : knownForNew[0]);
                              return {
                                ...d,
                                [row.key]: { provider, model: nextModel },
                              };
                            });
                          }}
                          data-testid={`spec-tier-provider-${row.key}`}
                        >
                          {providerOptions.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="flex flex-1 flex-col gap-1">
                        <span className="text-[11px] font-medium text-zinc-600">
                          Model
                        </span>
                        {/* Cue follow-up — model is a free string so
                            admins can configure new model ids the
                            backend hasn't catalogued yet (NVIDIA, the
                            self-hosted Local endpoint, any Custom
                            OpenAI-compatible deploy). The datalist
                            preserves discoverability for the curated
                            providers (anthropic / openai / google) by
                            offering catalogue suggestions inline. */}
                        <input
                          type="text"
                          className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-sm font-mono"
                          value={draft.model}
                          onChange={(e) =>
                            setDrafts((d) => ({
                              ...d,
                              [row.key]: { ...d[row.key], model: e.target.value },
                            }))
                          }
                          list={`spec-tier-model-options-${row.key}`}
                          placeholder="e.g. claude-sonnet-4-6"
                          data-testid={`spec-tier-model-${row.key}`}
                        />
                        <datalist id={`spec-tier-model-options-${row.key}`}>
                          {knownModels.map((m) => (
                            <option key={m} value={m} />
                          ))}
                        </datalist>
                      </label>
                    </div>
                    <p className="mt-2 text-[11px] text-zinc-400">
                      Default: {row.defaultProvider} / {row.defaultModel}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── Thinking budget ────────────────────────────── */}
          <section data-testid="spec-settings-reasoning">
            <h2 className="mb-1 text-base font-semibold text-zinc-900">
              Thinking budget
            </h2>
            <p className="mb-3 text-xs text-zinc-500">
              How much chain-of-thought reasoning models are asked for.
              This is the main control over how long drafting takes, and
              it applies to every module, not just the specification.
              Measured on Fireworks glm-5p3 writing one clause paragraph:
              provider default 66s, Low 5s, Medium 7s — with no
              difference in claim echo or repetition. High returned
              output identical to Medium.
            </p>
            <div className="flex items-center gap-3 rounded-sm border border-zinc-200 bg-white p-4">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-medium text-zinc-600">
                  Reasoning effort
                </span>
                <select
                  className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-sm"
                  value={defaultsDraft.reasoning_effort}
                  onChange={(e) =>
                    setDefaultsDraft((d) => ({
                      ...d, reasoning_effort: e.target.value,
                    }))
                  }
                  data-testid="spec-defaults-reasoning-effort"
                >
                  {REASONING_EFFORTS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <p className="text-xs text-zinc-500">
                Raise to Medium when you want richer mechanism on complex
                clauses and the disclosure supports it. A model that does
                not accept the setting is retried without it
                automatically.
              </p>
            </div>
          </section>

          {/* ── Spec defaults ──────────────────────────────── */}
          <section data-testid="spec-settings-defaults">
            <h2 className="mb-4 text-base font-semibold text-zinc-900">
              Spec defaults
            </h2>
            <div className="grid grid-cols-3 gap-3 rounded-sm border border-zinc-200 bg-white p-4">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-medium text-zinc-600">
                  Default jurisdiction
                </span>
                <select
                  className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-sm"
                  value={defaultsDraft.jurisdiction}
                  onChange={(e) =>
                    setDefaultsDraft((d) => ({
                      ...d,
                      jurisdiction: e.target.value as 'EP' | 'US' | 'IN',
                    }))
                  }
                  data-testid="spec-defaults-jurisdiction"
                >
                  <option value="EP">EP</option>
                  <option value="US">US</option>
                  <option value="IN">IN</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-medium text-zinc-600">
                  Word target (min)
                </span>
                <input
                  type="number"
                  min={0}
                  className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-sm"
                  value={defaultsDraft.word_target_min}
                  onChange={(e) =>
                    setDefaultsDraft((d) => ({
                      ...d,
                      word_target_min: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  data-testid="spec-defaults-word-target-min"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-medium text-zinc-600">
                  Word target (max)
                </span>
                <input
                  type="number"
                  min={0}
                  className="h-9 rounded-sm border border-zinc-300 bg-white px-2 text-sm"
                  value={defaultsDraft.word_target_max}
                  onChange={(e) =>
                    setDefaultsDraft((d) => ({
                      ...d,
                      word_target_max: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  data-testid="spec-defaults-word-target-max"
                />
              </label>
            </div>
            <p className="mb-2 mt-4 text-xs text-zinc-500">
              Word targets by jurisdiction. A blank pair inherits the
              default above. These apply to new matters; a matter
              already open keeps the targets it was created with, and
              the drafter can override them per section in the spec
              work area.
            </p>
            <div
              className="rounded-sm border border-zinc-200 bg-white"
              data-testid="spec-defaults-jurisdiction-targets"
            >
              {WORD_TARGET_JURISDICTIONS.map((code) => {
                const row = targetsDraft[code];
                const inheriting = row.min === '' && row.max === '';
                return (
                  <div
                    key={code}
                    className="flex items-center gap-3 border-b border-zinc-100 px-4 py-3 last:border-b-0"
                    data-testid={`spec-defaults-target-row-${code}`}
                  >
                    <span className="w-10 font-mono text-xs font-medium text-zinc-700">
                      {code}
                    </span>
                    <label className="flex flex-1 items-center gap-2">
                      <span className="text-[11px] text-zinc-500">min</span>
                      <input
                        type="number"
                        min={1}
                        className="h-8 w-28 rounded-sm border border-zinc-300 bg-white px-2 text-sm"
                        placeholder={String(defaultsDraft.word_target_min)}
                        value={row.min}
                        onChange={(e) =>
                          setTargetsDraft((t) => ({
                            ...t,
                            [code]: {
                              ...t[code],
                              min: e.target.value === ''
                                ? ''
                                : parseInt(e.target.value, 10) || '',
                            },
                          }))
                        }
                        data-testid={`spec-defaults-target-min-${code}`}
                      />
                    </label>
                    <label className="flex flex-1 items-center gap-2">
                      <span className="text-[11px] text-zinc-500">max</span>
                      <input
                        type="number"
                        min={1}
                        className="h-8 w-28 rounded-sm border border-zinc-300 bg-white px-2 text-sm"
                        placeholder={String(defaultsDraft.word_target_max)}
                        value={row.max}
                        onChange={(e) =>
                          setTargetsDraft((t) => ({
                            ...t,
                            [code]: {
                              ...t[code],
                              max: e.target.value === ''
                                ? ''
                                : parseInt(e.target.value, 10) || '',
                            },
                          }))
                        }
                        data-testid={`spec-defaults-target-max-${code}`}
                      />
                    </label>
                    <span className="w-24 text-right text-[11px] text-zinc-400">
                      {inheriting ? 'inherits default' : 'set'}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── Spec prompts (link out to the main Prompts admin) ── */}
          <section data-testid="spec-settings-prompts-link">
            <h2 className="mb-1 text-base font-semibold text-zinc-900">
              Spec prompts
            </h2>
            <p className="mb-3 text-xs text-zinc-500">
              Spec prompts can be edited in the main Prompts admin.
              The spec subset is filtered by stage='spec' there.
            </p>
            {onOpenPrompts && (
              <button
                type="button"
                className={outlineBtn}
                onClick={onOpenPrompts}
                data-testid="spec-settings-open-prompts"
              >
                <ExternalLink className="h-4 w-4" />
                Open Prompts admin →
              </button>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
